import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Receipt, Briefcase, User } from 'lucide-react';
import api from '@/lib/api';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { WORKSPACE_GROUPS, PUBLIC_TOOLS } from '@/lib/workspaceNav';

const ITEM = 'data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900 cursor-pointer';
const safe = (p) => p.then((r) => r.data || []).catch(() => []);

// Universal search across tools, quotes, customers, invoices and jobs (Ctrl/Cmd + K)
export default function CommandSearch({ open, onOpenChange }) {
  const nav = useNavigate();
  const [data, setData] = useState({ quotes: [], invoices: [], jobs: [] });

  useEffect(() => {
    if (!open) return;
    Promise.all([safe(api.get('/projects')), safe(api.get('/invoices')), safe(api.get('/jobs'))])
      .then(([quotes, invoices, jobs]) => setData({ quotes, invoices, jobs }));
  }, [open]);

  const go = (to) => { onOpenChange(false); nav(to); };
  const customers = [...new Map(data.quotes.filter((q) => q.customer_name).map((q) => [q.customer_name.toLowerCase(), q])).values()];
  const tools = [...WORKSPACE_GROUPS.flatMap((g) => g.items), ...PUBLIC_TOOLS];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search quotes, customers, jobs, invoices and tools…" data-testid="ws-search-input" />
      <CommandList className="max-h-[420px]" data-testid="ws-search-results">
        <CommandEmpty>No matches found.</CommandEmpty>
        <CommandGroup heading="Tools">
          {tools.map((t) => (
            <CommandItem key={`t-${t.to}-${t.label}`} value={`tool ${t.label}`} onSelect={() => go(t.to)} className={ITEM} data-testid={`ws-search-tool-${t.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <t.Icon /> {t.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {data.quotes.length > 0 && (
          <CommandGroup heading="Quotes">
            {data.quotes.slice(0, 200).map((q) => (
              <CommandItem key={`q-${q.id}`} value={`quote ${q.name} ${q.customer_name || ''} ${q.quote_number || ''}`} onSelect={() => go(`/estimator/${q.id}`)} className={ITEM}>
                <FileText /> <span className="truncate">{q.name}</span><span className="ml-auto text-xs text-slate-400">{q.customer_name || q.status}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {customers.length > 0 && (
          <CommandGroup heading="Customers">
            {customers.slice(0, 100).map((c) => (
              <CommandItem key={`c-${c.id}`} value={`customer ${c.customer_name}`} onSelect={() => go(`/customers?c=${encodeURIComponent(c.customer_name.trim().toLowerCase().replace(/\s+/g, " "))}`)} className={ITEM}>
                <User /> {c.customer_name}<span className="ml-auto text-xs text-slate-400">latest: {c.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {data.invoices.length > 0 && (
          <CommandGroup heading="Invoices">
            {data.invoices.slice(0, 200).map((i) => (
              <CommandItem key={`i-${i.id}`} value={`invoice ${i.invoice_number || ''} ${i.customer_name || ''}`} onSelect={() => go(`/invoices/${i.id}`)} className={ITEM}>
                <Receipt /> {i.invoice_number || 'Invoice'}<span className="ml-auto text-xs text-slate-400">{i.customer_name} · {i.status}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {data.jobs.length > 0 && (
          <CommandGroup heading="Jobs">
            {data.jobs.slice(0, 200).map((j) => (
              <CommandItem key={`j-${j.id}`} value={`job ${j.name} ${j.customer_name || ''}`} onSelect={() => go(`/jobs/${j.id}`)} className={ITEM}>
                <Briefcase /> {j.name}<span className="ml-auto text-xs text-slate-400">{j.customer_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
